import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, CheckCircle2, Users, Clock, TrendingUp, ArrowRight, Home, Shield, Target, Award, ShieldCheck, BadgeCheck, UserPlus, X, Menu, MapPin, Upload, FileText, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDropzone } from "react-dropzone";
import { LenderSelectItems } from "@/constants/lenders";
import { BH_LOCATION_OPTIONS } from "@/constants/locations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo and Image URLs
const CFI_LOGO_MAIN = "/images/cfi-logo-main.png";
const CFI_LOGO = "/images/cfi-logo.png";
const BETENBOUGH_LOGO = "/images/betenbough-logo-new.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_loan-ready-portal/artifacts/jdhz0poo_Untitled%20design%20-%202026-01-23T180713.643.png";




// Time zones for client's local time
const TIME_ZONES = [
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (No DST)" },
];

// Secondary lead types
const SECONDARY_LEAD_TYPES = [
  { value: "betenbough_referral", label: "Betenbough Referral" },
  { value: "mortgage_affiliate", label: "Mortgage Affiliate Referred" },
  { value: "realtor_affiliate", label: "Realtor Affiliate Referred" },
  { value: "past_client", label: "Past Client / Referral" },
  { value: "google_search", label: "Google Search" },
  { value: "social_media", label: "Social Media" },
  { value: "direct", label: "Direct / Walk-in" },
  { value: "other", label: "Other" },
];

// Error Boundary for third-party widgets
class WidgetErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.warn("Review widget error caught:", err.message); }
  render() { return this.state.hasError ? null : this.props.children; }
}

// CFI Review Widget Component - wrapped in error boundary to prevent third-party script crashes
const CFIReviewWidget = () => {
  const widgetRef = useRef(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    // Suppress postMessage errors from third-party widget scripts
    const handleError = (event) => {
      if (event.message && event.message.includes("postMessage")) {
        event.preventDefault();
        console.warn("Suppressed third-party postMessage error");
      }
    };
    window.addEventListener("error", handleError);

    const script = document.createElement('script');
    script.src = 'https://reputationhub.site/reputation/assets/review-widget.js';
    script.type = 'text/javascript';
    script.async = true;
    script.onerror = () => setLoadFailed(true);
    document.head.appendChild(script);

    return () => {
      window.removeEventListener("error", handleError);
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  if (loadFailed) return null;

  return (
    <WidgetErrorBoundary>
      <div ref={widgetRef} className="w-full">
        <iframe 
          className="lc_reviews_widget" 
          src="https://reputationhub.site/reputation/widgets/review_widget/niCJVilKzKOFQ9HmHKz2?widgetId=6973f27e23720e1ce71fb331" 
          frameBorder="0" 
          scrolling="no" 
          style={{ minWidth: '100%', width: '100%', minHeight: '400px' }}
          title="Credit Firm Inc Reviews"
          sandbox="allow-scripts allow-same-origin allow-popups"
        />
      </div>
    </WidgetErrorBoundary>
  );
};

const LandingPage = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [submittingClient, setSubmittingClient] = useState(false);
  const [betenboughReps, setBetenboughReps] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showRepRequestForm, setShowRepRequestForm] = useState(false);
  const [repRequestData, setRepRequestData] = useState({ name: "", email: "", phone: "" });
  const [submittingRepRequest, setSubmittingRepRequest] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [apiLenders, setApiLenders] = useState([]);
  const [apiLendersByLocation, setApiLendersByLocation] = useState({});
  const [clientFormData, setClientFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    betenbough_location: "",
    betenbough_sales_rep: "",
    betenbough_client_id_number: "",
    betenbough_affiliated_lender: "",
    betenbough_rep_notes: "",
    preferred_lender_notes: "",
    // Joint client
    has_joint_client: false,
    joint_first_name: "",
    joint_last_name: "",
    joint_email: "",
    joint_phone: "",
    // Status checkboxes - updated labels
    bh_home_tour_completed: false,
    loan_officer_introduction: false,
    loan_officer_meeting_completed: false,
    loan_officer_prequal_pending_credit: false,
    under_agreement: false,
  });

  // File upload dropzone
  const onDrop = useCallback((acceptedFiles) => {
    setUploadedFiles(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
    // Fetch reps on page load
    fetchBetenboughReps();
    fetchPreferredLenders();
  }, []);

  // Also fetch when modal opens (backup)
  useEffect(() => {
    if (showAddClientModal && betenboughReps.length === 0) {
      fetchBetenboughReps();
    }
  }, [showAddClientModal]);

  // Hardcoded fallback list of all Betenbough reps with full details
  const FALLBACK_BETENBOUGH_REPS = [
    // AMARILLO
    { name: "Brandon Bugg", role_title: "Sales Manager", location: "Amarillo", display_label: "Brandon Bugg – Sales Manager – Amarillo" },
    { name: "Caroline Watson", role_title: "Specialist", location: "Amarillo", display_label: "Caroline Watson – Specialist – Amarillo" },
    { name: "Miranda Moore", role_title: "Associate", location: "Amarillo", display_label: "Miranda Moore – Associate – Amarillo" },
    { name: "Courtney Grimes", role_title: "Specialist", location: "Amarillo", display_label: "Courtney Grimes – Specialist – Amarillo" },
    { name: "Stacie Roberts", role_title: "Associate", location: "Amarillo", display_label: "Stacie Roberts – Associate – Amarillo" },
    { name: "Kenia Cooke", role_title: "New Home Coordinator", location: "Amarillo", display_label: "Kenia Cooke – New Home Coordinator – Amarillo" },
    // LUBBOCK WEST
    { name: "Eddie Huskey", role_title: "Sales Manager", location: "Lubbock West", display_label: "Eddie Huskey – Sales Manager – Lubbock West" },
    { name: "Ben Vadder", role_title: "Specialist", location: "Lubbock West", display_label: "Ben Vadder – Specialist – Lubbock West" },
    { name: "Kiki Melhado", role_title: "Associate", location: "Lubbock West", display_label: "Kiki Melhado – Associate – Lubbock West" },
    { name: "Traci Sexton", role_title: "Specialist", location: "Lubbock West", display_label: "Traci Sexton – Specialist – Lubbock West" },
    { name: "Haley Beardemphl", role_title: "Associate", location: "Lubbock West", display_label: "Haley Beardemphl – Associate – Lubbock West" },
    { name: "Carly Morris", role_title: "Specialist", location: "Lubbock West", display_label: "Carly Morris – Specialist – Lubbock West" },
    { name: "Maddie Ritchie", role_title: "Associate", location: "Lubbock West", display_label: "Maddie Ritchie – Associate – Lubbock West" },
    { name: "Amelia Inglish", role_title: "New Home Coordinator", location: "Lubbock West", display_label: "Amelia Inglish – New Home Coordinator – Lubbock West" },
    // LUBBOCK NORTHWEST
    { name: "Jonathan Raitz", role_title: "Sales Manager", location: "Lubbock Northwest", display_label: "Jonathan Raitz – Sales Manager – Lubbock Northwest" },
    { name: "Kagan Massey", role_title: "Specialist", location: "Lubbock Northwest", display_label: "Kagan Massey – Specialist – Lubbock Northwest" },
    { name: "Sierra Simmons", role_title: "Associate", location: "Lubbock Northwest", display_label: "Sierra Simmons – Associate – Lubbock Northwest" },
    { name: "Maguire Mathis", role_title: "Specialist", location: "Lubbock Northwest", display_label: "Maguire Mathis – Specialist – Lubbock Northwest" },
    { name: "Scout Carter", role_title: "Associate", location: "Lubbock Northwest", display_label: "Scout Carter – Associate – Lubbock Northwest" },
    { name: "Halle Christiansen", role_title: "New Home Coordinator", location: "Lubbock Northwest", display_label: "Halle Christiansen – New Home Coordinator – Lubbock Northwest" },
    { name: "Adeline Carlyle", role_title: "New Home Coordinator", location: "Lubbock Northwest", display_label: "Adeline Carlyle – New Home Coordinator – Lubbock Northwest" },
    // LUBBOCK SOUTH
    { name: "Kathryn Miller", role_title: "Sales Manager", location: "Lubbock South", display_label: "Kathryn Miller – Sales Manager – Lubbock South" },
    { name: "Erin Boren", role_title: "Specialist", location: "Lubbock South", display_label: "Erin Boren – Specialist – Lubbock South" },
    { name: "Jay Proffitt", role_title: "Associate", location: "Lubbock South", display_label: "Jay Proffitt – Associate – Lubbock South" },
    { name: "Robin Lehman", role_title: "Specialist", location: "Lubbock South", display_label: "Robin Lehman – Specialist – Lubbock South" },
    { name: "Amber Delaney", role_title: "Associate", location: "Lubbock South", display_label: "Amber Delaney – Associate – Lubbock South" },
    { name: "Reese Carter", role_title: "New Home Coordinator", location: "Lubbock South", display_label: "Reese Carter – New Home Coordinator – Lubbock South" },
    // MIDLAND
    { name: "Michael Weehunt", role_title: "Sales Manager", location: "Midland", display_label: "Michael Weehunt – Sales Manager – Midland" },
    { name: "Kevin Bryant", role_title: "Specialist", location: "Midland", display_label: "Kevin Bryant – Specialist – Midland" },
    { name: "Jackeline Savino-Carmack", role_title: "Associate", location: "Midland", display_label: "Jackeline Savino-Carmack – Associate – Midland" },
    { name: "Amy Christian", role_title: "Specialist", location: "Midland", display_label: "Amy Christian – Specialist – Midland" },
    { name: "Yossy Flores", role_title: "Associate", location: "Midland", display_label: "Yossy Flores – Associate – Midland" },
    { name: "Layne French", role_title: "Specialist", location: "Midland", display_label: "Layne French – Specialist – Midland" },
    { name: "Jessica Castillo", role_title: "Associate", location: "Midland", display_label: "Jessica Castillo – Associate – Midland" },
    { name: "Moriama Vargas", role_title: "New Home Coordinator", location: "Midland", display_label: "Moriama Vargas – New Home Coordinator – Midland" },
    // ODESSA
    { name: "Chris Brazeal", role_title: "General Manager", location: "Odessa", display_label: "Chris Brazeal – General Manager – Odessa" },
    { name: "Jackie Fuentes", role_title: "Specialist", location: "Odessa", display_label: "Jackie Fuentes – Specialist – Odessa" },
    { name: "Chad Newman", role_title: "Associate", location: "Odessa", display_label: "Chad Newman – Associate – Odessa" },
    { name: "Juan Carlos Gaona", role_title: "Specialist", location: "Odessa", display_label: "Juan Carlos Gaona – Specialist – Odessa" },
    { name: "Dawn Miller", role_title: "Associate", location: "Odessa", display_label: "Dawn Miller – Associate – Odessa" },
    { name: "Aaron Salinas", role_title: "Specialist", location: "Odessa", display_label: "Aaron Salinas – Specialist – Odessa" },
    { name: "Cortnee Boen", role_title: "Associate", location: "Odessa", display_label: "Cortnee Boen – Associate – Odessa" },
    { name: "Azure House", role_title: "New Home Coordinator", location: "Odessa", display_label: "Azure House – New Home Coordinator – Odessa" },
  ];

  const fetchBetenboughReps = async () => {
    try {
      const response = await fetch(`${API}/lookup/betenbough-reps`);
      if (response.ok) {
        const data = await response.json();
        console.log("Fetched Betenbough reps:", data);
        // Use reps_detailed which includes name, role_title, location, and display_label
        if (data.reps_detailed && data.reps_detailed.length > 0) {
          setBetenboughReps(data.reps_detailed);
        } else if (data.reps && data.reps.length > 0) {
          // Fallback to simple names for backward compatibility
          setBetenboughReps(data.reps.map(name => ({ name, display_label: name })));
        } else {
          // Use hardcoded fallback if API returns empty
          console.log("Using hardcoded fallback reps list");
          setBetenboughReps(FALLBACK_BETENBOUGH_REPS);
        }
      } else {
        console.error("Failed to fetch reps, status:", response.status);
        setBetenboughReps(FALLBACK_BETENBOUGH_REPS);
      }
    } catch (error) {
      console.error("Error fetching Betenbough reps:", error);
      setBetenboughReps(FALLBACK_BETENBOUGH_REPS);
    }
  };

  const fetchPreferredLenders = async () => {
    try {
      const response = await fetch(`${API}/lookup/preferred-lenders`);
      if (response.ok) {
        const data = await response.json();
        setApiLenders(data.lenders || []);
        setApiLendersByLocation(data.by_location || {});
      }
    } catch (error) {
      console.error("Error fetching preferred lenders:", error);
    }
  };

  const handleClientFormChange = (field, value) => {
    setClientFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleClientFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!clientFormData.first_name || !clientFormData.last_name) {
      toast.error("First name and last name are required");
      return;
    }
    
    if (!clientFormData.phone) {
      toast.error("Phone number is required");
      return;
    }

    // Validate joint client fields if has_joint_client is checked
    if (clientFormData.has_joint_client) {
      if (!clientFormData.joint_first_name || !clientFormData.joint_last_name) {
        toast.error("Joint client first and last name are required");
        return;
      }
    }

    setSubmittingClient(true);
    try {
      // Build status string from checkboxes - updated labels
      const statusList = [];
      if (clientFormData.bh_home_tour_completed) statusList.push("Betenbough Homes New Home Tour Completed");
      if (clientFormData.loan_officer_introduction) statusList.push("Loan Officer Introduction");
      if (clientFormData.loan_officer_meeting_completed) statusList.push("Loan Officer Meeting Completed");
      if (clientFormData.loan_officer_prequal_pending_credit) statusList.push("Loan Officer - Pre Qual Pending Credit Repair");
      if (clientFormData.under_agreement) statusList.push("Under Agreement");

      // Prepare form data for file upload
      const formData = new FormData();
      
      // Add all form fields
      const clientData = {
        ...clientFormData,
        betenbough_client_status: statusList.join(", "),
        source: "betenbough_quick_add",
        corporate_manager: clientFormData.corporate_manager || "Lesa",
      };
      
      formData.append("data", JSON.stringify(clientData));
      
      // Add uploaded files
      uploadedFiles.forEach((file, index) => {
        formData.append(`files`, file);
      });

      const response = await fetch(`${API}/referrals/submit-with-files`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json().catch(() => null);
      if (response.ok || (result && result.success)) {
        toast.success("Client added successfully! Syncing to CRM...", { duration: 4000 });
        setShowAddClientModal(false);
        resetForm();
      } else {
        const errorMsg = result?.detail || "Failed to add client. Please try again.";
        toast.error(errorMsg, { duration: 6000 });
      }
    } catch (error) {
      console.error("Error adding client:", error);
      toast.error("Connection error — please check your internet and try again.", { duration: 6000 });
    } finally {
      setSubmittingClient(false);
    }
  };

  const resetForm = () => {
    setClientFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      betenbough_location: "",
      betenbough_sales_rep: "",
      betenbough_client_id_number: "",
      betenbough_affiliated_lender: "",
      betenbough_rep_notes: "",
      preferred_lender_notes: "",
      has_joint_client: false,
      joint_first_name: "",
      joint_last_name: "",
      joint_email: "",
      joint_phone: "",
      bh_home_tour_completed: false,
      loan_officer_introduction: false,
      loan_officer_meeting_completed: false,
      loan_officer_prequal_pending_credit: false,
      under_agreement: false,
    });
    setUploadedFiles([]);
    setShowRepRequestForm(false);
    setRepRequestData({ name: "", email: "", phone: "" });
  };

  const handleRepRequest = async (e) => {
    e.preventDefault();
    if (!repRequestData.name || !repRequestData.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setSubmittingRepRequest(true);
    try {
      const response = await fetch(`${API}/requests/betenbough-rep`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(repRequestData),
      });
      
      if (response.ok) {
        toast.success("Request submitted! We'll add your name to the list.");
        setShowRepRequestForm(false);
        setRepRequestData({ name: "", email: "", phone: "" });
      } else {
        toast.error("Failed to submit request");
      }
    } catch (error) {
      console.error("Error submitting rep request:", error);
      toast.error("Failed to submit request");
    } finally {
      setSubmittingRepRequest(false);
    }
  };

  const handleSurveyComplete = (data) => {
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-[#1b4541] sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <img 
                src={BETENBOUGH_LOGO} 
                alt="Betenbough Homes" 
                className="h-10 sm:h-12 w-auto" 
              />
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-3">
                <Button 
                  onClick={() => setShowAddClientModal(true)}
                  className="bg-[#779a86] text-white hover:bg-[#628a76]"
                  data-testid="quick-add-client-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Quick Add Client
                </Button>
                <div className="flex flex-col items-center">
                  <Link to="/login">
                    <Button 
                      variant="outline" 
                      className="border-[#c3ca6d] text-[#c3ca6d] hover:bg-[#c3ca6d] hover:text-[#1b4541]"
                      data-testid="login-btn"
                    >
                      Partner Login
                    </Button>
                  </Link>
                  <Link to="/register" className="text-xs text-[#c3ca6d]/80 hover:text-[#c3ca6d] mt-1">
                    Create Free Account
                  </Link>
                </div>
                <Link to="/login?type=client">
                  <Button 
                    className="bg-[#c3ca6d] text-[#1b4541] hover:bg-[#d4db7e]"
                    data-testid="client-login-btn"
                  >
                    Client Login
                  </Button>
                </Link>
              </div>

              {/* Mobile Navigation - always visible */}
              <div className="flex md:hidden items-center gap-1.5">
                <Button 
                  onClick={() => setShowAddClientModal(true)}
                  className="bg-[#779a86] text-white hover:bg-[#628a76] text-xs px-2.5 py-1.5 h-auto"
                  data-testid="success-mobile-quick-add-btn"
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1" />
                  Add Client
                </Button>
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    className="border-[#c3ca6d] text-[#c3ca6d] hover:bg-[#c3ca6d] hover:text-[#1b4541] text-xs px-2.5 py-1.5 h-auto"
                    data-testid="success-mobile-login-btn"
                  >
                    Partner
                  </Button>
                </Link>
                <Link to="/login?type=client">
                  <Button 
                    className="bg-[#c3ca6d] text-[#1b4541] hover:bg-[#d4db7e] text-xs px-2.5 py-1.5 h-auto"
                    data-testid="success-mobile-client-login-btn"
                  >
                    Client
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Add Client Modal for success view */}
        <Dialog open={showAddClientModal} onOpenChange={setShowAddClientModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pb-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1b4541] to-[#779a86] rounded-xl flex items-center justify-center">
                  <UserPlus className="h-6 w-6 text-white" />
                </div>
                <div>
                  <DialogTitle className="text-xl text-[#1b4541]">Quick Add New Client</DialogTitle>
                  <p className="text-sm text-slate-500 mt-1">Submit a new referral in seconds</p>
                </div>
              </div>
            </DialogHeader>
            
            <form onSubmit={handleClientFormSubmit} className="space-y-6 pt-4">
              {/* Section 1: Client Information */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                  <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">1</div>
                  Client Information
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                  <div>
                    <Label className="text-slate-700">First Name *</Label>
                    <Input
                      value={clientFormData.first_name}
                      onChange={(e) => handleClientFormChange("first_name", e.target.value)}
                      placeholder="John"
                      className="mt-1 border-slate-200 focus:border-[#1b4541]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Last Name *</Label>
                    <Input
                      value={clientFormData.last_name}
                      onChange={(e) => handleClientFormChange("last_name", e.target.value)}
                      placeholder="Doe"
                      className="mt-1 border-slate-200 focus:border-[#1b4541]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Email</Label>
                    <Input
                      type="email"
                      value={clientFormData.email}
                      onChange={(e) => handleClientFormChange("email", e.target.value)}
                      placeholder="john.doe@example.com"
                      className="mt-1 border-slate-200 focus:border-[#1b4541]"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Phone *</Label>
                    <Input
                      value={clientFormData.phone}
                      onChange={(e) => handleClientFormChange("phone", e.target.value)}
                      placeholder="(555) 123-4567"
                      className="mt-1 border-slate-200 focus:border-[#1b4541]"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Joint Client */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                  <div className="w-6 h-6 bg-[#779a86] rounded-full flex items-center justify-center text-xs text-white">2</div>
                  Joint Client (Optional)
                </div>
                <div className="pl-8 space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="s_has_joint"
                      checked={clientFormData.has_joint_client}
                      onCheckedChange={(checked) => handleClientFormChange("has_joint_client", checked)}
                    />
                    <Label htmlFor="s_has_joint" className="text-slate-700 cursor-pointer">Add Joint Client / Co-Applicant</Label>
                  </div>
                  
                  {clientFormData.has_joint_client && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border">
                      <div>
                        <Label className="text-slate-700">Joint Client First Name *</Label>
                        <Input
                          value={clientFormData.joint_first_name}
                          onChange={(e) => handleClientFormChange("joint_first_name", e.target.value)}
                          placeholder="First name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">Joint Client Last Name *</Label>
                        <Input
                          value={clientFormData.joint_last_name}
                          onChange={(e) => handleClientFormChange("joint_last_name", e.target.value)}
                          placeholder="Last name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">Joint Client Email</Label>
                        <Input
                          type="email"
                          value={clientFormData.joint_email}
                          onChange={(e) => handleClientFormChange("joint_email", e.target.value)}
                          placeholder="email@example.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">Joint Client Phone</Label>
                        <Input
                          value={clientFormData.joint_phone}
                          onChange={(e) => handleClientFormChange("joint_phone", e.target.value)}
                          placeholder="(555) 123-4567"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Section 3: Betenbough Details */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                  <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">3</div>
                  Betenbough Details
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                  <div>
                    <Label className="text-slate-700">Betenbough Location</Label>
                    <Select
                      value={clientFormData.betenbough_location}
                      onValueChange={(v) => handleClientFormChange("betenbough_location", v)}
                    >
                      <SelectTrigger className="mt-1 border-slate-200 focus:border-[#1b4541]">
                        <SelectValue placeholder="Select Location" />
                      </SelectTrigger>
                      <SelectContent>
                        {BH_LOCATION_OPTIONS.map((loc) => (
                          <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700">Betenbough Rep (Mandatory)</Label>
                    <Select
                      value={clientFormData.betenbough_sales_rep}
                      onValueChange={(v) => handleClientFormChange("betenbough_sales_rep", v)}
                    >
                      <SelectTrigger className="mt-1 border-slate-200 focus:border-[#1b4541]">
                        <SelectValue placeholder="Select Betenbough Rep" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {betenboughReps.length > 0 ? (
                          betenboughReps.map((rep, idx) => (
                            <SelectItem key={idx} value={rep.name}>{rep.display_label || rep.name}</SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="Brandon Bugg">Brandon Bugg – Sales Manager – Amarillo</SelectItem>
                            <SelectItem value="Dawn Miller">Dawn Miller – Associate – Odessa</SelectItem>
                            <SelectItem value="Layne French">Layne French – Specialist – Midland</SelectItem>
                            <SelectItem value="Maguire Mathis">Maguire Mathis – Specialist – Lubbock Northwest</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => setShowRepRequestForm(!showRepRequestForm)}
                      className="text-xs text-[#1b4541] hover:text-[#779a86] mt-1 underline"
                    >
                      Can't find your name?
                    </button>
                  </div>
                  
                  {showRepRequestForm && (
                    <div className="md:col-span-2 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-sm font-medium text-slate-700 mb-3">Request to be added</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Input
                          placeholder="Your Name *"
                          value={repRequestData.name}
                          onChange={(e) => setRepRequestData(prev => ({...prev, name: e.target.value}))}
                        />
                        <Input
                          type="email"
                          placeholder="Your Email *"
                          value={repRequestData.email}
                          onChange={(e) => setRepRequestData(prev => ({...prev, email: e.target.value}))}
                        />
                        <Input
                          placeholder="Your Phone"
                          value={repRequestData.phone}
                          onChange={(e) => setRepRequestData(prev => ({...prev, phone: e.target.value}))}
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={handleRepRequest}
                        disabled={submittingRepRequest}
                        className="mt-3 bg-[#1b4541] text-white text-sm"
                        size="sm"
                      >
                        {submittingRepRequest ? "Submitting..." : "Submit Request"}
                      </Button>
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-slate-700">Client Card ID Number (Mandatory if BH Rep)</Label>
                    <Input
                      value={clientFormData.betenbough_client_id_number}
                      onChange={(e) => handleClientFormChange("betenbough_client_id_number", e.target.value)}
                      placeholder="123456"
                      className="mt-1 border-slate-200 focus:border-[#1b4541]"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Preferred Lender (Optional)</Label>
                    <Select value={clientFormData.betenbough_affiliated_lender} onValueChange={(v) => handleClientFormChange("betenbough_affiliated_lender", v)}>
                      <SelectTrigger className="mt-1 border-slate-200 focus:border-[#1b4541]">
                        <SelectValue placeholder="Select Lender" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <LenderSelectItems location={clientFormData.betenbough_location} apiByLocation={apiLendersByLocation} apiLenders={apiLenders} testIdPrefix="success-lender" />
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Section 4: Status */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                  <div className="w-6 h-6 bg-[#779a86] rounded-full flex items-center justify-center text-xs text-white">4</div>
                  Client Status
                </div>
                <div className="pl-8 space-y-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox id="s_tour" checked={clientFormData.bh_home_tour_completed} onCheckedChange={(c) => handleClientFormChange("bh_home_tour_completed", c)} />
                    <Label htmlFor="s_tour" className="text-slate-700 cursor-pointer">Betenbough Homes New Home Tour Completed</Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox id="s_intro" checked={clientFormData.loan_officer_introduction} onCheckedChange={(c) => handleClientFormChange("loan_officer_introduction", c)} />
                    <Label htmlFor="s_intro" className="text-slate-700 cursor-pointer">Loan Officer Introduction</Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox id="s_meeting" checked={clientFormData.loan_officer_meeting_completed} onCheckedChange={(c) => handleClientFormChange("loan_officer_meeting_completed", c)} />
                    <Label htmlFor="s_meeting" className="text-slate-700 cursor-pointer">Loan Officer Meeting Completed</Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox id="s_prequal" checked={clientFormData.loan_officer_prequal_pending_credit} onCheckedChange={(c) => handleClientFormChange("loan_officer_prequal_pending_credit", c)} />
                    <Label htmlFor="s_prequal" className="text-slate-700 cursor-pointer">Loan Officer - Pre Qual Pending Credit Repair</Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox id="s_agreement" checked={clientFormData.under_agreement} onCheckedChange={(c) => handleClientFormChange("under_agreement", c)} />
                    <Label htmlFor="s_agreement" className="text-slate-700 cursor-pointer">Under Agreement</Label>
                  </div>
                </div>
              </div>

              {/* Section 5: Document Upload */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                  <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">5</div>
                  Document Upload
                </div>
                <div className="pl-8">
                  <p className="text-sm text-slate-600 mb-2">OPTIONAL: If Loan Officer, Please Upload Credit Report or Any File</p>
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${isDragActive ? "border-[#779a86] bg-[#779a86]/10" : "border-slate-300"}`}
                  >
                    <input {...getInputProps()} />
                    <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-600">Drag & drop or click to upload</p>
                    <p className="text-xs text-slate-400 mt-1">Multiple Files Allowed</p>
                  </div>
                  {uploadedFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {uploadedFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-500" />
                            <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                          </div>
                          <button type="button" onClick={() => removeFile(idx)} className="text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Section 6: Notes */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                  <div className="w-6 h-6 bg-[#779a86] rounded-full flex items-center justify-center text-xs text-white">6</div>
                  Notes
                </div>
                <div className="pl-8 space-y-4">
                  <div>
                    <Label className="text-slate-700">Betenbough Rep Notes</Label>
                    <Textarea
                      value={clientFormData.betenbough_rep_notes}
                      onChange={(e) => handleClientFormChange("betenbough_rep_notes", e.target.value)}
                      placeholder="Notes from Betenbough rep..."
                      className="mt-1 min-h-[80px]"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Preferred Lender Notes</Label>
                    <Textarea
                      value={clientFormData.preferred_lender_notes}
                      onChange={(e) => handleClientFormChange("preferred_lender_notes", e.target.value)}
                      placeholder="Notes from preferred lender..."
                      className="mt-1 min-h-[80px]"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowAddClientModal(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submittingClient} className="bg-gradient-to-r from-[#1b4541] to-[#779a86] text-white">
                  {submittingClient ? "Submitting..." : "Add Client"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Success Message */}
        <main className="max-w-3xl mx-auto px-6 py-20">
          <div className="text-center animate-fade-in">
            <div className="w-20 h-20 bg-[#c3ca6d] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-[#1b4541]" />
            </div>
            <h1 className="text-4xl font-bold text-[#1b4541] mb-4">
              You're One Step Closer to Home!
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
              Thank you for taking this important step toward homeownership. Our credit 
              counseling team will reach out within 24-48 hours to start your personalized 
              journey to becoming loan-ready.
            </p>
            
            <Card className="text-left mb-8 border-[#1b4541]/10">
              <CardHeader>
                <CardTitle className="text-lg text-[#1b4541]">What Happens Next?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#c3ca6d] rounded-full flex items-center justify-center text-sm font-semibold text-[#1b4541]">1</div>
                  <div>
                    <p className="font-medium text-[#1b4541]">Personal Review</p>
                    <p className="text-sm text-gray-600">A credit counselor reviews your information and creates a personalized plan.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#779a86] rounded-full flex items-center justify-center text-sm font-semibold text-white">2</div>
                  <div>
                    <p className="font-medium text-[#1b4541]">Your Counselor Calls</p>
                    <p className="text-sm text-gray-600">You'll receive a call to discuss your goals and create an action plan together.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#1b4541] rounded-full flex items-center justify-center text-sm font-semibold text-white">3</div>
                  <div>
                    <p className="font-medium text-[#1b4541]">Start Your Journey</p>
                    <p className="text-sm text-gray-600">Begin working toward loan-ready status with expert guidance every step of the way.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4 justify-center">
              <Link to="/">
                <Button 
                  variant="outline" 
                  className="border-[#1b4541] text-[#1b4541] hover:bg-[#1b4541] hover:text-white"
                  data-testid="back-to-home-btn"
                >
                  Back to Home Page
                </Button>
              </Link>
              <Link to="/login?type=client">
                <Button 
                  className="bg-[#c3ca6d] text-[#1b4541] hover:bg-[#d4db7e]"
                  data-testid="go-to-portal-btn"
                >
                  Track My Progress <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Add Client Modal */}
      <Dialog open={showAddClientModal} onOpenChange={setShowAddClientModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1b4541] to-[#779a86] rounded-xl flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl text-[#1b4541]">Quick Add New Client</DialogTitle>
                <p className="text-sm text-slate-500 mt-1">Submit a new referral in seconds</p>
              </div>
            </div>
          </DialogHeader>
          
          <form onSubmit={handleClientFormSubmit} className="space-y-6 pt-4">
            {/* Section 1: Client Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">1</div>
                Client Information
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                <div>
                  <Label htmlFor="modal_first_name" className="text-slate-700">First Name *</Label>
                  <Input
                    id="modal_first_name"
                    value={clientFormData.first_name}
                    onChange={(e) => handleClientFormChange("first_name", e.target.value)}
                    placeholder="John"
                    className="mt-1 border-slate-200 focus:border-[#1b4541]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="modal_last_name" className="text-slate-700">Last Name *</Label>
                  <Input
                    id="modal_last_name"
                    value={clientFormData.last_name}
                    onChange={(e) => handleClientFormChange("last_name", e.target.value)}
                    placeholder="Doe"
                    className="mt-1 border-slate-200 focus:border-[#1b4541]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="modal_email" className="text-slate-700">Email</Label>
                  <Input
                    id="modal_email"
                    type="email"
                    value={clientFormData.email}
                    onChange={(e) => handleClientFormChange("email", e.target.value)}
                    placeholder="john.doe@example.com"
                    className="mt-1 border-slate-200 focus:border-[#1b4541]"
                  />
                </div>
                <div>
                  <Label htmlFor="modal_phone" className="text-slate-700">Phone *</Label>
                  <Input
                    id="modal_phone"
                    value={clientFormData.phone}
                    onChange={(e) => handleClientFormChange("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-1 border-slate-200 focus:border-[#1b4541]"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Joint Client */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#779a86] rounded-full flex items-center justify-center text-xs text-white">2</div>
                Joint Client (Optional)
              </div>
              <div className="pl-8 space-y-4">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="has_joint_client"
                    checked={clientFormData.has_joint_client}
                    onCheckedChange={(checked) => handleClientFormChange("has_joint_client", checked)}
                  />
                  <Label htmlFor="has_joint_client" className="text-slate-700 cursor-pointer">Add Joint Client / Co-Applicant</Label>
                </div>
                
                {clientFormData.has_joint_client && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <Label className="text-slate-700">Joint Client First Name *</Label>
                      <Input
                        value={clientFormData.joint_first_name}
                        onChange={(e) => handleClientFormChange("joint_first_name", e.target.value)}
                        placeholder="First name"
                        className="mt-1 border-slate-200 focus:border-[#1b4541]"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Joint Client Last Name *</Label>
                      <Input
                        value={clientFormData.joint_last_name}
                        onChange={(e) => handleClientFormChange("joint_last_name", e.target.value)}
                        placeholder="Last name"
                        className="mt-1 border-slate-200 focus:border-[#1b4541]"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Joint Client Email</Label>
                      <Input
                        type="email"
                        value={clientFormData.joint_email}
                        onChange={(e) => handleClientFormChange("joint_email", e.target.value)}
                        placeholder="email@example.com"
                        className="mt-1 border-slate-200 focus:border-[#1b4541]"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Joint Client Phone</Label>
                      <Input
                        value={clientFormData.joint_phone}
                        onChange={(e) => handleClientFormChange("joint_phone", e.target.value)}
                        placeholder="(555) 123-4567"
                        className="mt-1 border-slate-200 focus:border-[#1b4541]"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section 3: Betenbough Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">3</div>
                Betenbough Details
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                <div>
                  <Label className="text-slate-700">Betenbough Location</Label>
                  <Select
                    value={clientFormData.betenbough_location}
                    onValueChange={(v) => handleClientFormChange("betenbough_location", v)}
                  >
                    <SelectTrigger className="mt-1 border-slate-200 focus:border-[#1b4541]">
                      <SelectValue placeholder="Select Location" />
                    </SelectTrigger>
                    <SelectContent>
                      {BH_LOCATION_OPTIONS.map((loc) => (
                        <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700">Betenbough Rep (Mandatory)</Label>
                  <Select
                    value={clientFormData.betenbough_sales_rep}
                    onValueChange={(v) => handleClientFormChange("betenbough_sales_rep", v)}
                  >
                    <SelectTrigger className="mt-1 border-slate-200 focus:border-[#1b4541]">
                      <SelectValue placeholder="Select Betenbough Rep" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {betenboughReps.length > 0 ? (
                        betenboughReps.map((rep, idx) => (
                          <SelectItem key={idx} value={rep.name}>{rep.display_label || rep.name}</SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="Brandon Bugg">Brandon Bugg – Sales Manager – Amarillo</SelectItem>
                          <SelectItem value="Dawn Miller">Dawn Miller – Associate – Odessa</SelectItem>
                          <SelectItem value="Layne French">Layne French – Specialist – Midland</SelectItem>
                          <SelectItem value="Maguire Mathis">Maguire Mathis – Specialist – Lubbock Northwest</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => setShowRepRequestForm(!showRepRequestForm)}
                    className="text-xs text-[#1b4541] hover:text-[#779a86] mt-1 underline"
                  >
                    Can't find your name?
                  </button>
                </div>
                
                {/* Rep Request Form */}
                {showRepRequestForm && (
                  <div className="md:col-span-2 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm font-medium text-slate-700 mb-3">Request to be added as a Betenbough Rep</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input
                        placeholder="Your Name *"
                        value={repRequestData.name}
                        onChange={(e) => setRepRequestData(prev => ({...prev, name: e.target.value}))}
                        className="border-slate-200"
                      />
                      <Input
                        type="email"
                        placeholder="Your Email *"
                        value={repRequestData.email}
                        onChange={(e) => setRepRequestData(prev => ({...prev, email: e.target.value}))}
                        className="border-slate-200"
                      />
                      <Input
                        placeholder="Your Phone"
                        value={repRequestData.phone}
                        onChange={(e) => setRepRequestData(prev => ({...prev, phone: e.target.value}))}
                        className="border-slate-200"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleRepRequest}
                      disabled={submittingRepRequest}
                      className="mt-3 bg-[#1b4541] hover:bg-[#143632] text-white text-sm"
                      size="sm"
                    >
                      {submittingRepRequest ? "Submitting..." : "Submit Request"}
                    </Button>
                  </div>
                )}
                
                <div>
                  <Label className="text-slate-700">Client Card ID Number (Mandatory if BH Rep)</Label>
                  <Input
                    value={clientFormData.betenbough_client_id_number}
                    onChange={(e) => handleClientFormChange("betenbough_client_id_number", e.target.value)}
                    placeholder="123456"
                    className="mt-1 border-slate-200 focus:border-[#1b4541]"
                  />
                </div>
                <div>
                  <Label className="text-slate-700">Preferred Lender (Optional)</Label>
                  <Select value={clientFormData.betenbough_affiliated_lender} onValueChange={(v) => handleClientFormChange("betenbough_affiliated_lender", v)}>
                    <SelectTrigger className="mt-1 border-slate-200 focus:border-[#1b4541]">
                      <SelectValue placeholder="Select Lender" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <LenderSelectItems location={clientFormData.betenbough_location} apiByLocation={apiLendersByLocation} apiLenders={apiLenders} testIdPrefix="refer-lender" />
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section 4: Client Status */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#779a86] rounded-full flex items-center justify-center text-xs text-white">4</div>
                Client Status
              </div>
              <div className="pl-8 space-y-3">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="bh_home_tour"
                    checked={clientFormData.bh_home_tour_completed}
                    onCheckedChange={(checked) => handleClientFormChange("bh_home_tour_completed", checked)}
                  />
                  <Label htmlFor="bh_home_tour" className="text-slate-700 cursor-pointer">Betenbough Homes New Home Tour Completed</Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="loan_officer_intro"
                    checked={clientFormData.loan_officer_introduction}
                    onCheckedChange={(checked) => handleClientFormChange("loan_officer_introduction", checked)}
                  />
                  <Label htmlFor="loan_officer_intro" className="text-slate-700 cursor-pointer">Loan Officer Introduction</Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="loan_officer_meeting"
                    checked={clientFormData.loan_officer_meeting_completed}
                    onCheckedChange={(checked) => handleClientFormChange("loan_officer_meeting_completed", checked)}
                  />
                  <Label htmlFor="loan_officer_meeting" className="text-slate-700 cursor-pointer">Loan Officer Meeting Completed</Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="loan_officer_prequal"
                    checked={clientFormData.loan_officer_prequal_pending_credit}
                    onCheckedChange={(checked) => handleClientFormChange("loan_officer_prequal_pending_credit", checked)}
                  />
                  <Label htmlFor="loan_officer_prequal" className="text-slate-700 cursor-pointer">Loan Officer - Pre Qual Pending Credit Repair</Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="under_agreement"
                    checked={clientFormData.under_agreement}
                    onCheckedChange={(checked) => handleClientFormChange("under_agreement", checked)}
                  />
                  <Label htmlFor="under_agreement" className="text-slate-700 cursor-pointer">Under Agreement</Label>
                </div>
              </div>
            </div>

            {/* Section 5: Document Upload */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">5</div>
                Document Upload
              </div>
              <div className="pl-8">
                <p className="text-sm text-slate-600 mb-2">OPTIONAL: If Loan Officer, Please Upload Credit Report or Any File Type</p>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isDragActive ? "border-[#779a86] bg-[#779a86]/10" : "border-slate-300 hover:border-[#779a86]"
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600">Drag & drop or click to upload</p>
                  <p className="text-xs text-slate-400 mt-1">Multiple Files Allowed</p>
                </div>
                
                {uploadedFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-500" />
                          <span className="text-sm text-slate-700 truncate max-w-[200px]">{file.name}</span>
                          <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Section 6: Notes */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#779a86] rounded-full flex items-center justify-center text-xs text-white">6</div>
                Notes
              </div>
              <div className="pl-8 space-y-4">
                <div>
                  <Label className="text-slate-700">Betenbough Rep Notes</Label>
                  <Textarea
                    value={clientFormData.betenbough_rep_notes}
                    onChange={(e) => handleClientFormChange("betenbough_rep_notes", e.target.value)}
                    placeholder="Notes from Betenbough rep..."
                    className="mt-1 border-slate-200 focus:border-[#1b4541] min-h-[80px]"
                  />
                </div>
                <div>
                  <Label className="text-slate-700">Preferred Lender Notes</Label>
                  <Textarea
                    value={clientFormData.preferred_lender_notes}
                    onChange={(e) => handleClientFormChange("preferred_lender_notes", e.target.value)}
                    placeholder="Notes from preferred lender..."
                    className="mt-1 border-slate-200 focus:border-[#1b4541] min-h-[80px]"
                  />
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddClientModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingClient}
                className="bg-gradient-to-r from-[#1b4541] to-[#779a86] hover:from-[#143632] hover:to-[#628a76] text-white px-6"
              >
                {submittingClient ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Client
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Header - Credit Firm Inc Partner Portal */}
      <header className="bg-[#1b4541] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={CFI_LOGO_MAIN} 
                alt="Credit Firm Inc" 
                className="h-10 sm:h-12 w-auto" 
              />
              <div className="hidden sm:block border-l border-white/30 pl-3">
                <span className="text-[#c3ca6d] text-xs font-medium">Betenbough Partner Portal</span>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-3">
              <Button 
                onClick={() => setShowAddClientModal(true)}
                className="bg-[#779a86] text-white hover:bg-[#628a76]"
                data-testid="header-quick-add-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Quick Add Client
              </Button>
              <Link to="/login">
                <Button 
                  variant="outline" 
                  className="border-[#c3ca6d] text-[#c3ca6d] hover:bg-[#c3ca6d] hover:text-[#1b4541]"
                  data-testid="header-login-btn"
                >
                  Partner Login
                </Button>
              </Link>
              <Link to="/login?type=client">
                <Button 
                  className="bg-[#c3ca6d] text-[#1b4541] hover:bg-[#d4db7e]"
                  data-testid="header-client-login-btn"
                >
                  Client Login
                </Button>
              </Link>
            </div>

            {/* Mobile Navigation - always visible */}
            <div className="flex md:hidden items-center gap-1.5">
              <Button 
                onClick={() => setShowAddClientModal(true)}
                className="bg-[#779a86] text-white hover:bg-[#628a76] text-xs px-2.5 py-1.5 h-auto"
                data-testid="mobile-quick-add-btn"
              >
                <UserPlus className="h-3.5 w-3.5 mr-1" />
                Add Client
              </Button>
              <Link to="/login">
                <Button 
                  variant="outline" 
                  className="border-[#c3ca6d] text-[#c3ca6d] hover:bg-[#c3ca6d] hover:text-[#1b4541] text-xs px-2.5 py-1.5 h-auto"
                  data-testid="mobile-login-btn"
                >
                  Partner
                </Button>
              </Link>
              <Link to="/login?type=client">
                <Button 
                  className="bg-[#c3ca6d] text-[#1b4541] hover:bg-[#d4db7e] text-xs px-2.5 py-1.5 h-auto"
                  data-testid="mobile-client-login-btn"
                >
                  Client
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Partner Focused */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1b4541] via-[#1b4541] to-[#234e48] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#c3ca6d]/20 text-[#c3ca6d] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="h-4 w-4" />
                Credit Firm Inc × Betenbough Homes Partner Portal
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Helping Betenbough Clients Become{" "}
                <span className="text-[#c3ca6d]">Loan-Ready</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-6 max-w-xl">
                Welcome to the Credit Firm Inc partner portal for Betenbough Homes sales representatives. 
                We work together to help your clients improve their credit and qualify for home financing.
              </p>
              <div className="bg-white/10 rounded-lg p-4 mb-8 border border-white/20">
                <h3 className="text-[#c3ca6d] font-semibold mb-2 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  How We Support Your Clients
                </h3>
                <ul className="text-white/80 text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#c3ca6d] mt-0.5 flex-shrink-0" />
                    <span>Personalized credit counseling and improvement plans</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#c3ca6d] mt-0.5 flex-shrink-0" />
                    <span>Direct communication with their preferred lenders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#c3ca6d] mt-0.5 flex-shrink-0" />
                    <span>Progress tracking through our partner portal</span>
                  </li>
                </ul>
              </div>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => setShowAddClientModal(true)}
                  size="lg" 
                  className="bg-[#c3ca6d] text-[#1b4541] hover:bg-[#d4db7e] font-semibold"
                  data-testid="hero-quick-add-btn"
                >
                  <UserPlus className="mr-2 h-5 w-5" />
                  Quick Add Client
                </Button>
                <Link to="/login">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="text-white border-white/30 hover:bg-white/10"
                    data-testid="hero-login-btn"
                  >
                    Partner Login <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <img 
                src={HERO_IMAGE} 
                alt="Family achieving homeownership with improved credit score" 
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Partner Quick Guide Section */}
      <section className="py-12 bg-[#f7f8f3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-[#1b4541] mb-2">Partner Quick Guide</h2>
            <p className="text-[#779a86]">Two easy ways to add and track your client referrals</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 border border-[#1b4541]/10 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#c3ca6d] rounded-lg flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-[#1b4541]" />
                </div>
                <h3 className="font-semibold text-[#1b4541]">Quick Add Client</h3>
              </div>
              <p className="text-slate-600 text-sm mb-4">
                Use the "Quick Add Client" button to instantly submit a new referral. Fill in client details, 
                select your Betenbough location, and add any notes for our credit counselors.
              </p>
              <Button 
                onClick={() => setShowAddClientModal(true)}
                className="w-full bg-[#779a86] text-white hover:bg-[#628a76]"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Quick Add Client
              </Button>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[#1b4541]/10 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#1b4541] rounded-lg flex items-center justify-center">
                  <Target className="h-5 w-5 text-[#c3ca6d]" />
                </div>
                <h3 className="font-semibold text-[#1b4541]">Partner Portal Login</h3>
              </div>
              <p className="text-slate-600 text-sm mb-4">
                Log in to view all your referred clients, track their progress toward loan-ready status, 
                and communicate with our credit counseling team.
              </p>
              <Link to="/login" className="block">
                <Button 
                  variant="outline"
                  className="w-full border-[#1b4541] text-[#1b4541] hover:bg-[#1b4541] hover:text-white"
                >
                  Partner Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-8 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-[#1b4541]">
              <ShieldCheck className="h-6 w-6 text-[#779a86]" />
              <span className="text-sm font-medium">Trusted by Betenbough</span>
            </div>
            <div className="flex items-center gap-2 text-[#1b4541]">
              <BadgeCheck className="h-6 w-6 text-[#c3ca6d]" />
              <span className="text-sm font-medium">BBB Accredited</span>
            </div>
            <div className="flex items-center gap-2 text-[#1b4541]">
              <Star className="h-6 w-6 text-[#c3ca6d] fill-[#c3ca6d]" />
              <span className="text-sm font-medium">5-Star Reviews</span>
            </div>
            <div className="flex items-center gap-2 text-[#1b4541]">
              <Shield className="h-6 w-6 text-[#779a86]" />
              <span className="text-sm font-medium">Secure & Confidential</span>
            </div>
          </div>
        </div>
      </section>

      {/* How CFI Helps Section */}
      <section id="how-it-works" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1b4541] mb-4">
              How Credit Firm Inc Helps Your Clients
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our proven process helps Betenbough clients move from "not quite ready" to "loan-ready" 
              so they can qualify for financing with their preferred lender.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1b4541] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-[#c3ca6d]">1</div>
              <h3 className="font-semibold text-[#1b4541] mb-2">You Submit Referral</h3>
              <p className="text-sm text-gray-600">Add your client via Quick Add or portal with their contact info and status</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#779a86] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white">2</div>
              <h3 className="font-semibold text-[#1b4541] mb-2">We Contact Client</h3>
              <p className="text-sm text-gray-600">Our counselor reaches out within 24-48 hours to begin their assessment</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#c3ca6d] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-[#1b4541]">3</div>
              <h3 className="font-semibold text-[#1b4541] mb-2">Credit Improvement</h3>
              <p className="text-sm text-gray-600">Personalized plan to improve credit scores and address any issues</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1b4541] rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="h-8 w-8 text-[#c3ca6d]" />
              </div>
              <h3 className="font-semibold text-[#1b4541] mb-2">Loan Ready</h3>
              <p className="text-sm text-gray-600">Client qualifies with their lender and moves forward with Betenbough</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Our Partnership */}
      <section className="py-16 md:py-20 bg-[#f7f8f3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1b4541] mb-6">
                Credit Firm Inc: Your Partner in Client Success
              </h2>
              <p className="text-gray-600 mb-6 text-lg">
                Credit Firm Inc has been helping individuals and families improve their credit 
                for over 30 years. We specialize in helping Betenbough Homes clients become 
                loan-ready through personalized credit counseling.
              </p>
              <p className="text-gray-600 mb-8">
                Our team works directly with your clients and their preferred lenders to ensure 
                they have the best path forward to homeownership. You can track every client's 
                progress through our partner portal.
              </p>
              <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-[#1b4541]/10">
                <img src={BETENBOUGH_LOGO} alt="Betenbough Homes" className="h-12 w-auto" />
                <div>
                  <p className="font-semibold text-[#1b4541]">Betenbough Homes</p>
                  <p className="text-sm text-gray-500">Our Affiliate Partner</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-white border-[#1b4541]/10 card-hover">
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 bg-[#c3ca6d] rounded-lg flex items-center justify-center mb-3">
                    <Clock className="h-6 w-6 text-[#1b4541]" />
                  </div>
                  <CardTitle className="text-lg text-[#1b4541]">Fast Response</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">We contact your clients within 24-48 hours of referral submission.</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-[#1b4541]/10 card-hover">
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 bg-[#779a86] rounded-lg flex items-center justify-center mb-3">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-lg text-[#1b4541]">Dedicated Support</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">Each client gets a personal credit counselor who guides them throughout.</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-[#1b4541]/10 card-hover">
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 bg-[#1b4541] rounded-lg flex items-center justify-center mb-3">
                    <TrendingUp className="h-6 w-6 text-[#c3ca6d]" />
                  </div>
                  <CardTitle className="text-lg text-[#1b4541]">Track Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">Monitor your clients' journey through our real-time partner portal.</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-[#1b4541]/10 card-hover">
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 bg-[#c3ca6d] rounded-lg flex items-center justify-center mb-3">
                    <Award className="h-6 w-6 text-[#1b4541]" />
                  </div>
                  <CardTitle className="text-lg text-[#1b4541]">Proven Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">Thousands of clients have become loan-ready with our help.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section - CFI Widget */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1b4541] mb-4">
              What Clients Say About Credit Firm Inc
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real reviews from families who've worked with our team to achieve their homeownership dreams.
            </p>
          </div>
          <CFIReviewWidget />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1b4541] text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <img src={CFI_LOGO_MAIN} alt="Credit Firm Inc" className="h-12 w-auto mb-4" />
              <p className="text-white/60 text-sm">
                Expert credit counseling services helping families achieve homeownership.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#c3ca6d] mb-4">Affiliate Partner</h4>
              <div className="flex items-center gap-3">
                <img src={BETENBOUGH_LOGO} alt="Betenbough Homes" className="h-10 w-auto" />
              </div>
              <p className="text-white/60 text-sm mt-2">
                Building homes and communities across West Texas
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#c3ca6d] mb-4">Quick Links</h4>
              <div className="space-y-2">
                <a href="#how-it-works" className="block text-white/60 hover:text-[#c3ca6d] text-sm transition-colors">How It Works</a>
                <Link to="/login" className="block text-white/60 hover:text-[#c3ca6d] text-sm transition-colors">Partner Login</Link>
                <Link to="/login?type=client" className="block text-white/60 hover:text-[#c3ca6d] text-sm transition-colors">Client Login</Link>
                <Link to="/cfi" className="block text-white/60 hover:text-[#c3ca6d] text-sm transition-colors">CFI Main Site</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} Credit Firm Inc. All rights reserved.
            </p>
            <p className="text-white/40 text-sm">
              Betenbough Partner Portal
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
